import { useEffect, useMemo } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { useEnsurePartnerLink } from "@/hooks/use-ensure-partner-link";
import { Navigate, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { usePartnerProfile } from "@/hooks/use-partner-profile";
import { usePartnerMetrics } from "@/hooks/use-partner-metrics";
import { usePartnerDeals } from "@/hooks/use-partner-deals";
import { usePartnerOrders } from "@/hooks/use-partner-orders";
import { usePartnerPayments } from "@/hooks/use-partner-payments";
import { 
  TrendingUp, 
  Target, 
  Users,
  FileCheck,
  Coins,
  Plus,
  Eye,
  Clock,
  Package,
  TrendingDown,
  ArrowRight,
  User,
  Calendar,
  MapPin,
  Truck,
  CheckCircle,
  PoundSterling,
  Download,
  Check,
  X,
  Badge as BadgeIcon
} from "lucide-react";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useUpdatePartnerPayment } from "@/hooks/use-partner-payments";
import { format } from "date-fns";
import { OrderCard } from "@/components/partner/OrderCard";
import { generateCommissionInvoice } from "@/components/partner/CommissionInvoiceGenerator";
import { useSyncPartnerProfile } from "@/hooks/use-sync-partner-profile";

const PartnerPortal = () => {
  const { data: hasCrmAccess, isLoading } = useCrmAccess();
  const { session } = useAuth();
  const navigate = useNavigate();

  // Ensure partner linking
  const { 
    isCheckingLink, 
    isLinking, 
    linkError, 
    needsLinking, 
    isLinked,
    partnerEmail 
  } = useEnsurePartnerLink();

  // Auto-sync partner profile if needed
  useSyncPartnerProfile();

  // Get partner profile using the enhanced hook
  const { data: partnerProfile, isLoading: profileLoading } = usePartnerProfile(session?.user?.id);
  
  // Get partner metrics based on profile
  const { data: metrics } = usePartnerMetrics(partnerProfile?.id);
  
  // Get partner deals and orders for additional insights
  const { data: partnerDeals } = usePartnerDeals(partnerProfile?.id || "");
  const { data: partnerOrders } = usePartnerOrders(partnerProfile?.id);
  const { data: partnerPayments } = usePartnerPayments(partnerProfile?.id || "");

  console.log("🔍 PartnerPortal Debug:", {
    isLoading,
    profileLoading,
    hasCrmAccess,
    partnerProfile: partnerProfile ? "found" : "null",
    sessionUserId: session?.user?.id,
    isCheckingLink,
    isLinking,
    needsLinking,
    isLinked,
    partnerEmail,
    linkError: linkError?.message
  });

  useEffect(() => {
    // Clear pending invitation if user has CRM access (invitation completed)
    if (session?.user && hasCrmAccess === true) {
      try {
        sessionStorage.removeItem('pendingInvitation');
      } catch (e) {
        console.warn('Could not clear sessionStorage:', e);
      }
    }
    
    // If user is logged in but lacks CRM access, try to route them to complete invite if we have it
    if (session?.user && hasCrmAccess === false) {
      try {
        const stored = sessionStorage.getItem('pendingInvitation');
        if (stored) {
          const { token, partnerId } = JSON.parse(stored || '{}') || {};
          if (token) {
            const params = new URLSearchParams({ token });
            if (partnerId) params.set('partner_id', String(partnerId));
            navigate(`/partner-invite?${params.toString()}`, { replace: true });
            return;
          }
        }
      } catch {}
    }
  }, [session?.user, hasCrmAccess, navigate]);

  // Hooks that must run on every render (cannot be after early returns)
  
  // Safe array processing with better error handling and logging
  const safePartnerOrders = (() => {
    console.log("🔍 Processing partnerOrders:", { partnerOrders, type: typeof partnerOrders, isArray: Array.isArray(partnerOrders) });
    return Array.isArray(partnerOrders) ? partnerOrders : [];
  })();
  
  const safePartnerDeals = (() => {
    console.log("🔍 Processing partnerDeals:", { partnerDeals, type: typeof partnerDeals, isArray: Array.isArray(partnerDeals) });
    return Array.isArray(partnerDeals) ? partnerDeals : [];
  })();
  
  const safePartnerPayments = (() => {
    console.log("🔍 Processing partnerPayments:", { partnerPayments, type: typeof partnerPayments, isArray: Array.isArray(partnerPayments) });
    return Array.isArray(partnerPayments) ? partnerPayments : [];
  })();

  const commissionsPerOrder = useMemo(() => {
    if (!safePartnerOrders || !partnerProfile) return [] as any[];
    
    console.log('🔍 Debug: Processing orders', { orderCount: safePartnerOrders.length, profileRate: partnerProfile.commission_rate });
    
    return safePartnerOrders.map((order, index) => {
      try {
        const commissionRate = (partnerProfile.commission_rate || 0) / 100;
        const revenue = order.sale?.total_amount || 0;
        const commissionAmount = revenue * commissionRate;
        const customerName = order.sale?.deal?.contact ? 
          `${order.sale.deal.contact.first_name} ${order.sale.deal.contact.last_name}` : 
          'Unknown Customer';
        const isPaid = order.sale?.status === 'paid';
        
        const result = {
          orderId: order.id,
          customerName,
          revenue,
          commissionAmount,
          date: order.created_at,
          monthYear: format(new Date(order.created_at), 'MMM yyyy'),
          order,
          isPaid
        };
        
        console.log(`🔍 Order ${index} processed:`, result);
        return result;
      } catch (error) {
        console.error(`❌ Error processing order ${index}:`, error, order);
        return null as any;
      }
    }).filter(Boolean).sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [safePartnerOrders, partnerProfile]);

  const updatePaymentMutation = useUpdatePartnerPayment();

  if (isLoading || profileLoading || isCheckingLink || isLinking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">
            {isLinking ? "Connecting your partner account..." : 
             isCheckingLink ? "Verifying partner access..." :
             "Loading partner portal..."}
          </p>
        </div>
      </div>
    );
  }

  // Show link error if linking failed
  if (linkError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error linking partner account: {linkError.message}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!hasCrmAccess) {
    // Check if there's a pending invitation - show loading state
    try {
      const stored = sessionStorage.getItem('pendingInvitation');
      if (stored) {
        const { token } = JSON.parse(stored || '{}') || {};
        if (token) {
          return (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
                <p className="text-slate-600">Completing your partner access...</p>
              </div>
            </div>
          );
        }
      }
    } catch {}
    
    console.log("❌ No CRM access - redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  if (!partnerProfile) {
    console.log("❌ No partner profile found - redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  // Normalize data arrays to avoid TS unknown errors
  const ordersArr = Array.isArray(partnerOrders) ? partnerOrders : [] as any[];
  const dealsArr = Array.isArray(partnerDeals) ? partnerDeals : [] as any[];
  const paymentsArr = Array.isArray(partnerPayments) ? partnerPayments : [] as any[];
  
  // Calculate enhanced metrics using real-time order data for consistency
  const paidOrdersCommission = partnerProfile ? ordersArr
    .filter(order => order.sale?.status === 'paid')
    .reduce((total, order) => {
      const commissionRate = (partnerProfile.commission_rate || 0) / 100;
      const revenue = order.sale?.total_amount || 0;
      return total + (revenue * commissionRate);
    }, 0) : 0;
  const commissionEarned = paidOrdersCommission;
  const totalDeals = dealsArr.length || 0;
  const wonDeals = partnerProfile?.deals_won || 0;
  const conversionRate = totalDeals > 0 ? (wonDeals / totalDeals) * 100 : 0;
  const avgDealValue = wonDeals > 0 ? (partnerProfile?.revenue_generated || 0) / wonDeals : 0;
  const goalProgress = (partnerProfile?.goal_revenue || 0) > 0 ? ((partnerProfile?.revenue_generated || 0) / (partnerProfile?.goal_revenue || 0)) * 100 : 0;
  const goalDealsProgress = (partnerProfile?.goal_deals || 0) > 0 ? ((partnerProfile?.deals_won || 0) / (partnerProfile?.goal_deals || 0)) * 100 : 0;
  
  // Prepare data for tabs
  const opportunities = dealsArr.filter((deal: any) => deal.status !== 'won' && deal.status !== 'lost').slice(0, 5);
  const proposals = dealsArr.filter((deal: any) => deal.status === 'proposal_sent').slice(0, 5);
  const orders = ordersArr.slice(0, 5);
  
  // Calculate commission data per order - with payment status and null safety  
  // moved commissionsPerOrder useMemo earlier

  // Group commissions by month/year
  console.log('🔍 Debug: commissionsPerOrder', commissionsPerOrder);
  
  const commissionsByMonth = commissionsPerOrder.reduce((acc, commission) => {
    if (!acc[commission.monthYear]) {
      acc[commission.monthYear] = [];
    }
    acc[commission.monthYear].push(commission);
    return acc;
  }, {} as Record<string, typeof commissionsPerOrder>);
  
  console.log('🔍 Debug: commissionsByMonth', commissionsByMonth);
  const commissionsEntries = Object.entries(commissionsByMonth) as [string, any[]][];

  // moved updatePaymentMutation earlier

  // Helper functions for commission management
  const generateInvoiceForMonth = (monthYear: string, commissions: any[]) => {
    if (!partnerProfile || commissions.length === 0) return;
    
    // Only include paid commissions in the invoice
    const paidCommissions = commissions.filter(commission => commission.isPaid);
    if (paidCommissions.length === 0) {
      console.warn('No paid commissions found for invoice generation');
      return;
    }
    
    generateCommissionInvoice(paidCommissions, partnerProfile, monthYear);
  };

  const toggleCommissionPaid = async (commissionId: string, paid: boolean) => {
    // This would need to be implemented with actual payment tracking
    // For now, we'll show the checkbox functionality
    console.log(`Commission ${commissionId} marked as ${paid ? 'paid' : 'unpaid'}`);
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.partnerPortal} />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <Button 
                className="font-semibold rounded-lg shadow-md text-black"
                style={{ backgroundColor: '#DFEF97' }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#d1e587'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#DFEF97'}
                onClick={() => navigate("/dashboard/crm/contacts")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
              <Button 
                className="font-semibold rounded-lg shadow-md text-black"
                style={{ backgroundColor: '#DFEF97' }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#d1e587'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#DFEF97'}
                onClick={() => navigate("/dashboard/crm/deals?action=create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Opportunity
              </Button>
              <Button 
                variant="outline" 
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
                onClick={() => navigate("/dashboard/partner-orders")}
              >
                <Package className="h-4 w-4 mr-2" />
                View Orders
              </Button>
            </div>

            {/* Performance Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Number of Contacts</CardTitle>
                    <Users className="h-5 w-5 text-teal-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {metrics?.contactsCount || 0}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Number of Proposals</CardTitle>
                    <FileCheck className="h-5 w-5 text-purple-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {metrics?.proposalsCount || 0}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Deals Won</CardTitle>
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {partnerProfile.deals_won || 0}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Revenue Generated</CardTitle>
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-800">
                    £{(partnerProfile.revenue_generated || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    £{avgDealValue.toLocaleString()} avg deal value
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Commission Earned</CardTitle>
                    <Coins className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-800">
                    £{commissionEarned.toFixed(2)}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {partnerProfile.commission_rate || 0}% commission rate
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Goals Progress */}
            {(partnerProfile.goal_revenue || partnerProfile.goal_deals) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {partnerProfile.goal_revenue && (
                  <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-semibold text-slate-700">Revenue Goal Progress</CardTitle>
                        <Target className="h-5 w-5 text-teal-600" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-2xl font-bold text-slate-800">
                          £{(partnerProfile.revenue_generated || 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-slate-500">
                          of £{(partnerProfile.goal_revenue || 0).toLocaleString()}
                        </div>
                      </div>
                      <Progress value={Math.min(goalProgress, 100)} className="h-2" />
                      <div className="text-sm text-slate-500 mt-1">
                        {goalProgress.toFixed(1)}% complete
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {partnerProfile.goal_deals && (
                  <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-semibold text-slate-700">Deals Goal Progress</CardTitle>
                        <Target className="h-5 w-5 text-purple-600" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-2xl font-bold text-slate-800">
                          {partnerProfile.deals_won || 0}
                        </div>
                        <div className="text-sm text-slate-500">
                          of {partnerProfile.goal_deals} deals
                        </div>
                      </div>
                      <Progress value={Math.min(goalDealsProgress, 100)} className="h-2" />
                      <div className="text-sm text-slate-500 mt-1">
                        {goalDealsProgress.toFixed(1)}% complete
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Activity Tabs */}
            <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-slate-800">Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="opportunities" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger 
                      value="opportunities" 
                      className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black"
                    >
                      Opportunities
                    </TabsTrigger>
                    <TabsTrigger 
                      value="proposals"
                      className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black"  
                    >
                      Proposals
                    </TabsTrigger>
                    <TabsTrigger 
                      value="orders"
                      className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black"
                    >
                      Orders
                    </TabsTrigger>
                    <TabsTrigger 
                      value="commissions"
                      className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black"
                    >
                      Commissions
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="opportunities" className="mt-4">
                    {opportunities.length > 0 ? (
                      <div className="space-y-3">
                        {opportunities.map((deal) => (
                          <div 
                            key={deal.id} 
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => navigate(`/dashboard/crm/deals/${deal.id}`)}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-slate-800 truncate">{deal.title}</div>
                              <div className="text-sm text-slate-500">
                                {deal.contact?.first_name && deal.contact?.last_name 
                                  ? `${deal.contact.first_name} ${deal.contact.last_name}`
                                  : 'No contact'
                                }
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-slate-800">
                                £{(deal.value || 0).toLocaleString()}
                              </div>
                              <div className="text-sm px-2 py-1 rounded bg-blue-100 text-blue-800">
                                {deal.status}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Target className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>No active opportunities</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="proposals" className="mt-4">
                    {proposals.length > 0 ? (
                      <div className="space-y-3">
                        {proposals.map((deal) => (
                          <div 
                            key={deal.id} 
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => navigate(`/dashboard/crm/deals/${deal.id}`)}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-slate-800 truncate">{deal.title}</div>
                              <div className="text-sm text-slate-500">
                                {deal.contact?.first_name && deal.contact?.last_name 
                                  ? `${deal.contact.first_name} ${deal.contact.last_name}`
                                  : 'No contact'
                                }
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-slate-800">
                                £{(deal.value || 0).toLocaleString()}
                              </div>
                              <div className="text-sm px-2 py-1 rounded bg-purple-100 text-purple-800">
                                Proposal Sent
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <FileCheck className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>No proposals sent</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="orders" className="mt-4">
                    {orders.length > 0 ? (
                      <div className="space-y-3">
                        {orders.map((order) => (
                          <OrderCard
                            key={order.id}
                            order={order}
                            onClick={() => navigate(`/dashboard/partner-orders`)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Package className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>No recent orders</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="commissions" className="mt-4">
                    {(() => {
                      console.log('🔍 Rendering commissions tab. Keys:', Object.keys(commissionsByMonth));
                      console.log('🔍 Commission data length:', Object.keys(commissionsByMonth).length);
                      return null;
                    })()}
                    {Object.keys(commissionsByMonth).length > 0 ? (
                      <div className="space-y-6">
                        {commissionsEntries.map(([monthYear, commissions]) => {
                          console.log(`🔍 Processing month ${monthYear} with ${commissions.length} commissions`);
                          
                          try {
                            const paidCommissions = commissions.filter(c => c.isPaid);
                            const unpaidCommissions = commissions.filter(c => !c.isPaid);
                            const totalPaidCommission = paidCommissions.reduce((sum, c) => sum + c.commissionAmount, 0);
                            const totalUnpaidCommission = unpaidCommissions.reduce((sum, c) => sum + c.commissionAmount, 0);
                            const totalRevenue = commissions.reduce((sum, c) => sum + c.revenue, 0);
                          
                          return (
                            <div key={monthYear} className="border border-slate-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h4 className="text-lg font-semibold text-slate-800">{monthYear}</h4>
                                  <p className="text-sm text-slate-500">
                                    {commissions.length} orders • £{totalRevenue.toFixed(2)} revenue
                                  </p>
                                </div>
                                <div className="flex items-center gap-3">
                                  {/* Commission Status Buttons */}
                                  <div className="flex items-center gap-2">
                                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                      <div className="text-sm font-medium text-green-800">
                                        £{totalPaidCommission.toFixed(2)}
                                      </div>
                                      <div className="text-xs text-green-600">Due</div>
                                    </div>
                                    {totalUnpaidCommission > 0 && (
                                      <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                        <div className="text-sm font-medium text-red-800">
                                          £{totalUnpaidCommission.toFixed(2)}
                                        </div>
                                        <div className="text-xs text-red-600">Pending</div>
                                      </div>
                                    )}
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateInvoiceForMonth(monthYear, commissions)}
                                    disabled={paidCommissions.length === 0}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Invoice
                                  </Button>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="grid grid-cols-7 gap-4 text-xs font-medium text-slate-500 uppercase tracking-wide pb-2 border-b">
                                  <div>Customer</div>
                                  <div>Revenue</div>
                                  <div>Commission</div>
                                  <div>Date</div>
                                  <div>Customer Paid</div>
                                  <div>Commission Due</div>
                                  <div>Commission Paid</div>
                                </div>
                                
                                {commissions.map((commission) => (
                                  <div key={commission.orderId} className="grid grid-cols-7 gap-4 items-center py-2 text-sm">
                                    <div className="font-medium text-slate-700">
                                      {commission.customerName}
                                    </div>
                                    <div className="text-slate-600">
                                      £{commission.revenue.toFixed(2)}
                                    </div>
                                    <div className={`font-semibold ${commission.isPaid ? 'text-green-600' : 'text-red-600'}`}>
                                      £{commission.commissionAmount.toFixed(2)}
                                    </div>
                                    <div className="text-slate-500">
                                      {format(new Date(commission.date), 'dd/MM/yyyy')}
                                    </div>
                                    <div className="flex items-center">
                                      {commission.isPaid ? (
                                        <div className="flex items-center text-green-600">
                                          <CheckCircle className="h-4 w-4 mr-1" />
                                          <span className="text-xs font-medium">Yes</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center text-red-600">
                                          <X className="h-4 w-4 mr-1" />
                                          <span className="text-xs font-medium">No</span>
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <Checkbox
                                        checked={commission.isPaid}
                                        disabled={!commission.isPaid} // Only enable for paid orders
                                        onCheckedChange={(checked) => 
                                          toggleCommissionPaid(commission.orderId, checked as boolean)
                                        }
                                      />
                                    </div>
                                    <div>
                                      <Checkbox
                                        checked={false} // For future Xero integration
                                        onCheckedChange={(checked) => {
                                          // TODO: Integrate with Xero for commission payment tracking
                                          console.log(`Commission paid status for ${commission.orderId}:`, checked);
                                        }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                          } catch (error) {
                            console.error(`❌ Error rendering month ${monthYear}:`, error);
                            return (
                              <div key={monthYear} className="p-4 border border-red-200 rounded-lg bg-red-50">
                                <p className="text-red-600">Error loading commissions for {monthYear}</p>
                              </div>
                            );
                          }
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Coins className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>No commission data available</p>
                        <p className="text-xs mt-1">Commission details will appear here when orders are completed.</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default PartnerPortal;